/**
 * Input travels over ONE persistent WebSocket per session.
 *
 * A request per pointer event was the latency floor: every move paid a full round trip and they
 * queued behind each other — and behind the video download, under the browser's per-host connection
 * limit — so a flick arrived at the phone as a slow drag. A frame on an open socket costs almost
 * nothing.
 *
 * **There is no HTTP fallback any more.** There was one, and it cost us: when the service refused the
 * socket (it only accepted service keys, so every tester arriving through the platform was rejected),
 * control silently switched to a request per event and kept working. Nobody noticed for hours — the
 * only visible symptom was that sound, which has no fallback, went quiet. A degraded path that hides a
 * broken one is worse than a visible failure, so a dead socket now says so and retries.
 */

import { explainHint } from './hint'
import type { TikTokHubApi } from '../api/client'
import type { Dict } from '../i18n/en'

export type InputAction =
  | 'touch_down'
  | 'touch_move'
  | 'touch_up'
  | 'tap'
  | 'swipe'
  | 'text'
  | 'key'

export interface InputChannelOptions {
  api: TikTokHubApi
  sessionId: string
  t: Dict
  onStatus?: (text: string, bad?: boolean) => void
  /** The service closed the socket with 1008 (policy): this session may no longer own the phone. The
   *  socket does not say why; the panel asks over HTTP. No reconnect — an ended session stays ended. */
  onRefused?: () => void
}

export class InputChannel {
  private ws: WebSocket | null = null
  private timer: number | null = null
  private retries = 0
  private disposed = false

  constructor(private readonly o: InputChannelOptions) {}

  open(): void {
    this.close()
    this.disposed = false
    try {
      const ws = new WebSocket(this.o.api.wsUrl(`/v1/sessions/${this.o.sessionId}/input-ws`))
      this.ws = ws
      ws.onopen = () => {
        this.retries = 0
        this.o.onStatus?.(this.o.t.input.channelOpen)
      }
      // **The service answers when a tap does not land** (ported from Device Lab). The socket was
      // write-only here, so a dispatch the machine refused — or one that timed out on a busy hub —
      // reached the person as silence, indistinguishable from an app that ignored them.
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(String(e.data)) as { type?: string; action?: string; hint?: string }
          if (msg?.type === 'input_failed') {
            this.o.onStatus?.(this.o.t.input.notDelivered(this.label(msg.action as InputAction), explainHint(msg.hint ?? '', this.o.t.input.xiaomiSecurity)), true)
          }
        } catch {
          //: a frame we do not understand is not a reason to drop a working channel
        }
      }
      // Reconnect, and say so. Control is the product: silence here used to be covered by the HTTP
      // fallback, which is exactly how a refused socket went unnoticed.
      ws.onclose = (e) => {
        if (this.ws !== ws) return
        this.ws = null
        if (e.code === 1008 && this.o.onRefused) {
          this.disposed = true
          this.o.onRefused()
          return
        }
        this.reconnect()
      }
      ws.onerror = () => {
        if (this.ws !== ws) return
        this.ws = null
        this.reconnect()
      }
    } catch {
      this.ws = null
      this.reconnect()
    }
  }

  /** Backs off to 4s: a session whose socket is refused must not hammer the service either. */
  private reconnect(): void {
    if (this.disposed || this.timer !== null) return
    const wait = Math.min(4000, 250 * 2 ** this.retries)
    this.retries += 1
    this.o.onStatus?.(this.o.t.input.reconnecting, true)
    this.timer = window.setTimeout(() => {
      this.timer = null
      if (!this.disposed) this.open()
    }, wait)
  }

  close(): void {
    this.disposed = true
    if (this.timer !== null) {
      window.clearTimeout(this.timer)     // a closed session must stop dialling
      this.timer = null
    }
    const ws = this.ws
    this.ws = null
    if (ws) {
      try {
        ws.close()
      } catch {
        /* already closing */
      }
    }
  }

  /** "release" reads to a tester; "touch_up ·" told them nothing. */
  private label(action: InputAction): string {
    return this.o.t.input[action]
  }

  send(action: InputAction, params: Record<string, unknown> = {}): void {
    if (this.disposed) return
    const ws = this.ws
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ action, params }))
      this.o.onStatus?.(this.o.t.input.sent(this.label(action))) // nothing to wait for
      return
    }
    // Dropped on purpose rather than sent another way: a tap that arrives seconds later, out of order
    // with the gesture it belonged to, is worse than one that never arrives — and the visible failure
    // is what tells the tester (and us) that the channel is down.
    this.o.onStatus?.(this.o.t.input.channelDown, true)
  }
}
